import humps from 'humps'
import { useCallback, useEffect, useMemo, useRef, type PropsWithChildren } from 'react'

import { getConsumer } from '@/cable/consumer'
import AnswerStreamCtx from '@/contexts/AnswerStreamContext'
import type { CableEvent, CableEventListener } from '@/types/cable'

export default function AnswerStreamProvider({ children }: PropsWithChildren) {
  const listenersRef = useRef<Set<CableEventListener>>(new Set())
  const pendingRef = useRef<Map<string, string>>(new Map())

  const subscribe = useCallback((listener: CableEventListener) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const markPending = useCallback((conversationId: string, userMessageId: string) => {
    pendingRef.current.set(conversationId, userMessageId)
  }, [])

  const clearPending = useCallback((conversationId: string) => {
    pendingRef.current.delete(conversationId)
  }, [])

  const getPending = useCallback(
    (conversationId: string) => pendingRef.current.get(conversationId),
    [],
  )

  useEffect(() => {
    const consumer = getConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: 'AnswerStreamChannel' },
      {
        received: (data: unknown) => {
          const event = humps.camelizeKeys(data as Record<string, unknown>) as CableEvent
          ;[...listenersRef.current].forEach((listener) => listener(event))
        },
      },
    )
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const value = useMemo(
    () => ({ subscribe, markPending, clearPending, getPending }),
    [subscribe, markPending, clearPending, getPending],
  )
  return <AnswerStreamCtx.Provider value={value}>{children}</AnswerStreamCtx.Provider>
}
